/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";
import Immutable from "immutable";
import DOMPurify from "dompurify";

import * as DashboardUtils from "js/dashboards/utils";
import * as Overlays from "js/dashboards/components/overlays";
import SimpleTextInput from "js/common/views/inputs/simple-text-input";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import ReactQuill from "react-quill";
// Quill ships no styling of its own; react-quill expects the theme CSS to be imported
// once. Without it the toolbar picker/expand SVGs render at their full intrinsic size
// (the giant arrows). This is global, but it only adds proper toolbar/picker styling —
// the CubeTV big-screen overrides in app.css still win by cascade order where they set
// backgrounds/heights, so the CubeTV free-text editor is unaffected.
import "react-quill/dist/quill.snow.css";
import PanelBackgroundImageField from "js/dashboards/components/text-panel-image-field";

// Creation default for NEW panels (seeded into dataConfig by the catalog). The read
// fallback for panels missing the key is "fit" (see getTextMode) so legacy Heading
// panels render unchanged. Flip this one constant to change the new-panel default.
export const DEFAULT_TEXT_MODE = "rich";

export const getTextMode = (config) => config.get("textMode") || "fit";
export const getBackgroundImage = (config) => {
  const image = config.get("backgroundImage");
  return image ? image.toJS() : null;
};

// Quill leaves markup like "<p><br></p>" when emptied; treat rich content as empty
// when it has no visible text (our toolbar has no media embeds, so text is the signal).
export const isRichTextEmpty = (html) =>
  DOMPurify.sanitize(html || "", {ALLOWED_TAGS: [], ALLOWED_ATTR: []}).replace(/\s|&nbsp;/g, "") === "";

const backgroundStyleFor = (image) => image && image.url
  ? {
    backgroundImage: `url("${encodeURI(image.url)}")`,
    backgroundSize: image.fit === "contain" ? "contain" : "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center"
  }
  : {};

const borderMap = {
  "FULL": {border: `1px solid rgba(0,0,0,0.35)`, padding: "0.5rem"},
  "NONE": {border: "none"},
  "RIGHT": {border: "none", borderRight: `1px solid rgba(0,0,0,0.35)`, paddingRight: "10px"},
  "LEFT": {border: "none", borderLeft: `1px solid rgba(0,0,0,0.35)`, paddingLeft: "10px"},
  "TOP": {border: "none", borderTop: `1px solid rgba(0,0,0,0.35)`, paddingTop: "10px"},
  "BOTTOM": {border: "none", borderBottom: `1px solid rgba(0,0,0,0.35)`, paddingBottom: "10px"}
};

// The app's only global .ql-* CSS is a set of CubeTV big-screen overrides in app.css
// (e.g. `.ql-editor p { font-size: 6rem }`, `.ql-container { min-height: 560px }`,
// grey toolbar/pickers). Those leak into this panel's editor and rendered output. These
// scoped styles win on specificity within our wrapper (emotion prefixes them with the
// generated class), so the editor and panel render at normal sizes, while the imported
// quill.snow.css base theme handles the toolbar/pickers.
const editorRichTextScope = (theme) => css`
  & .ql-toolbar.ql-snow {
    height: auto;
    background: transparent;
    border: 1px solid rgba(128, 128, 128, 0.3);
    border-bottom: none;
    border-radius: 4px 4px 0 0;
  }
  & .ql-snow.ql-toolbar .ql-picker .ql-picker-label,
  & .ql-snow.ql-toolbar .ql-picker .ql-picker-options {
    background-color: ${theme.palette.background.card};
    color: ${theme.palette.text.main};
  }
  & .ql-snow.ql-toolbar .ql-picker .ql-picker-options {
    border: 1px solid ${theme.palette.border.light};
  }
  /* Quill's snow theme hardcodes dark (#444) icon strokes/fills and picker text, which
     disappear on a dark toolbar. Drive them from the theme so they show in both modes.
     Active buttons keep quill's blue highlight (its selector out-specifies these). */
  & .ql-toolbar.ql-snow .ql-picker { color: ${theme.palette.text.main}; }
  & .ql-toolbar.ql-snow .ql-stroke { stroke: ${theme.palette.text.main}; }
  & .ql-toolbar.ql-snow .ql-fill,
  & .ql-toolbar.ql-snow .ql-stroke.ql-fill { fill: ${theme.palette.text.main}; }
  /* app.css forces .ql-snow .ql-formats to vertical-align: top, which lifts the icon
     buttons above the taller picker dropdowns; restore quill's default middle. */
  & .ql-snow .ql-formats {
    vertical-align: middle;
  }
  /* Foundation's global "button" base (app.css) adds margin: 0 0 1.25em to every
     <button>, including the toolbar's B/I/U/list buttons but not the <span> pickers.
     That makes the button groups ~20px taller and lifts their glyphs above the picker
     text. Quill already resets padding/border/background (it out-specifies Foundation),
     so only the margin leaks through — zero it. */
  & .ql-toolbar.ql-snow button {
    margin: 0;
  }
  & .ql-container.ql-snow {
    min-height: 220px;
    max-height: 440px;
    font-size: 14px;
    color: ${theme.palette.text.main};
    border: 1px solid rgba(128, 128, 128, 0.3);
    border-radius: 0 0 4px 4px;
  }
  & .ql-editor {
    min-height: 220px;
  }
  & .ql-editor p,
  & .ql-editor ul,
  & .ql-editor ol {
    font-size: 14px;
  }
  & .ql-editor .ql-size-small { font-size: 0.75em; }
  & .ql-editor .ql-size-large { font-size: 1.5em; }
  & .ql-editor .ql-size-huge { font-size: 2.5em; }
`;

// Applied to the rendered panel output (the div is itself `.ql-editor`), so the `&`
// selectors carry the extra class specificity needed to beat the global CubeTV rules.
const rendererRichTextScope = css`
  &.ql-editor { padding: 0; }
  &.ql-editor p,
  &.ql-editor ul,
  &.ql-editor ol { font-size: 14px; }
  &.ql-editor .ql-size-small { font-size: 0.75em; }
  &.ql-editor .ql-size-large { font-size: 1.5em; }
  &.ql-editor .ql-size-huge { font-size: 2.5em; }
`;

const Text = React.memo(({
  dimensions,
  config,
  isEditing,
  data,
  component
}) => {
  const mode = getTextMode(config);
  const image = getBackgroundImage(config);
  const bgStyle = backgroundStyleFor(image);
  const hasImage = !!(image && image.url);

  const text = config.get("text");
  const richText = config.get("richText");
  const hasContent = mode === "rich" ? !isRichTextEmpty(richText) : !!text;

  if (!hasContent && !hasImage) {
    return <Overlays.Message coverOtherContent={false}>
      Enter some text
    </Overlays.Message>;
  }

  if (mode === "rich") {
    const clean = DOMPurify.sanitize(richText || "");
    return <div
        className="ql-editor"
        css={rendererRichTextScope}
        style={{
          height: dimensions.height,
          width: "100%",
          overflow: "auto",
          cursor: "default",
          ...bgStyle,
          ...borderMap[config.get("textBorder")]
        }}
        dangerouslySetInnerHTML={{__html: clean}} />;
  }

  const border = config.get("textBorder");
  const alignment = config.get("textAlignment") || "LEFT";
  const estimatedLetterHeight = component.getIn(["layout", "height"]) < 2 ? 16 : 28;
  const rowsAvailable = Math.floor(dimensions.height / estimatedLetterHeight);
  const calculatedFontSize = component.getIn(["layout", "height"]) < 2 ? 13 : 22;

  return (
      <span
          style={{
            display: "block",
            position: "relative",
            containerType: "inline-size",
            height: dimensions.height,
            // Fit mode is a short heading that the overlay drag handle would sit on top of,
            // so inset the text in edit mode to keep the handle "out" beside it. Rich mode
            // is a full panel and aligns flush-left like other panel types.
            marginLeft: isEditing ? 30 : 0,
            marginRight: isEditing ? 50 : 0,
            cursor: "default",
            ...bgStyle,
            ...borderMap[border]
          }}>
        {text && <span
            style={{
              cursor: "default",
              textAlign: alignment.toLowerCase(),
              fontSize: calculatedFontSize,
              position: "absolute",
              width: "calc(100%)",
              overflow: "hidden",
              lineHeight: `${estimatedLetterHeight}px`,
              display: "-webkit-box",
              maxWidth: "calc(100%)",
              // When rowsAvailable is NaN (dimensions.height initially undefined) the
              // browser complains, so only set the clamp when we have a height.
              ...(dimensions.height ? {WebkitLineClamp: rowsAvailable} : {}),
              WebkitBoxOrient: "vertical",
              left: 0,
              top: 0
            }}>
          {text}
        </span>}
      </span>
  );
});

const QUILL_MODULES = {
  toolbar: [
    [{"font": [false, "serif", "monospace"]}],
    [{"size": ["small", false, "large", "huge"]}],
    [{"align": [false, "center", "right", "justify"]}],
    ["bold", "italic", "strike", "underline"],
    [{"color": []}, {"background": []}],
    [{"list": "ordered"}, {"list": "bullet"}]
  ]
};

const TextEditor = React.memo(({
  component,
  onComponentChange
}) => {
  const config = component.get("dataConfig");
  const mode = getTextMode(config);

  const {theme} = React.useContext(CustomThemeContext);

  const onConfigChange = React.useCallback(newDataConfig => {
    onComponentChange(component.set("dataConfig", newDataConfig));
  }, [onComponentChange, component]);

  const onModeChange = (nextMode) => onConfigChange(config.set("textMode", nextMode));
  const onBorderChange = (border) => onConfigChange(config.set("textBorder", border));
  const onAlignmentChange = (alignment) => onConfigChange(config.set("textAlignment", alignment));
  const onRichTextChange = (html) => onConfigChange(config.set("richText", html));
  const onBackgroundImageChange = (image) =>
    onConfigChange(image ? config.set("backgroundImage", Immutable.fromJS(image)) : config.delete("backgroundImage"));

  // Match the label styling used by other panel edit slideouts.
  const labelStyle = {
    color: theme.palette.text.main,
    fontFamily: "Montserrat, Verdana, sans-serif",
    fontSize: 14,
    fontWeight: 700
  };

  const switcherStyle = css`
    border-radius: 8px;
    background: ${theme.palette.background.card};
    border: 1px solid ${theme.palette.background.paper};
    box-shadow: 0px 2px 2px rgb(0 0 0 / 2%);
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    position: relative;
    z-index: 1;
  `;

  // Highlights when `active` so it works for the mode toggle (effective mode) as well
  // as border/alignment (compared against the stored value by the caller).
  const panelStyle = (active) => css`
    background: ${active ? theme.themeId === "light" ? "#e4ecf7" : "#000" : "transparent"};
    display: flex;
    justify-content: center;
    flex-grow: 1;
    font-size: 16px;
    text-align: center;
    padding: 10px 10px;
    cursor: pointer;
    border-left: 1px solid ${theme.palette.background.paper};

    &:first-of-type {
      border-left: none;
    }

    &:hover {
      background: ${theme.palette.background.paper};
    }
  `;

  const borderIconStyle = {height: 20, width: 20};
  const border = config.get("textBorder");
  const alignment = config.get("textAlignment");

  // Rich editor and image field want room to breathe; the drawer is already ~95% wide
  // but the fit controls stay narrow, so only the wide sections use the wider column.
  const wideSectionStyle = {width: "100%", maxWidth: 760, marginBottom: "1rem"};
  const narrowSectionStyle = {width: 300, marginBottom: "1rem"};

  // Borders apply to both fit and rich modes, so share one control between them.
  const bordersSection = (
      <div style={narrowSectionStyle}>
        <label style={labelStyle}>Borders</label>
        <div css={switcherStyle}>
          <span css={panelStyle(border === "FULL")} onClick={() => onBorderChange("FULL")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.primary.main}`}} />
          </span>
          <span css={panelStyle(border === "LEFT")} onClick={() => onBorderChange("LEFT")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.border.light}`,
              borderLeft: `2px solid ${theme.palette.primary.main}`}} />
          </span>
          <span css={panelStyle(border === "RIGHT")} onClick={() => onBorderChange("RIGHT")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.border.light}`,
              borderRight: `2px solid ${theme.palette.primary.main}`}} />
          </span>
          <span css={panelStyle(border === "TOP")} onClick={() => onBorderChange("TOP")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.border.light}`,
              borderTop: `2px solid ${theme.palette.primary.main}`}} />
          </span>
          <span css={panelStyle(border === "BOTTOM")} onClick={() => onBorderChange("BOTTOM")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.border.light}`,
              borderBottom: `2px solid ${theme.palette.primary.main}`}} />
          </span>
          <span css={panelStyle(border === "NONE")} onClick={() => onBorderChange("NONE")}>
            <div style={{...borderIconStyle, border: `2px solid ${theme.palette.border.light}`}} />
          </span>
        </div>
      </div>
  );

  return <div style={{padding: "0.5rem 1rem"}}>
    <div style={narrowSectionStyle}>
      <label style={labelStyle}>Display</label>
      <div css={switcherStyle}>
        <span data-test-id="mode-toggle-fit" css={panelStyle(mode === "fit")}
              onClick={() => onModeChange("fit")}>Fit text to panel</span>
        <span data-test-id="mode-toggle-rich" css={panelStyle(mode === "rich")}
              onClick={() => onModeChange("rich")}>Rich text editor</span>
      </div>
    </div>

    {mode === "fit" ? <>
      <div style={narrowSectionStyle}>
        <label style={labelStyle}>Text</label>
        <SimpleTextInput
            data-test-id="text-box"
            placeholder="Text"
            value={config.get("text")}
            onChange={(e) => onConfigChange(config.set("text", e))} />
      </div>
      {bordersSection}
      <div style={narrowSectionStyle}>
        <label style={labelStyle}>Alignment</label>
        <div css={switcherStyle}>
          <span css={panelStyle(alignment === "LEFT")} onClick={() => onAlignmentChange("LEFT")}>
            <i className="fa fa-align-left" />
          </span>
          <span css={panelStyle(alignment === "CENTER")} onClick={() => onAlignmentChange("CENTER")}>
            <i className="fa fa-align-center" />
          </span>
          <span css={panelStyle(alignment === "RIGHT")} onClick={() => onAlignmentChange("RIGHT")}>
            <i className="fa fa-align-right" />
          </span>
        </div>
      </div>
    </> : <>
      <div style={wideSectionStyle} css={editorRichTextScope(theme)}>
        <label style={labelStyle}>Content</label>
        <ReactQuill theme="snow" value={config.get("richText") || ""}
                    modules={QUILL_MODULES} onChange={onRichTextChange} />
      </div>
      {bordersSection}
    </>}

    <div style={wideSectionStyle}>
      <label style={labelStyle}>Background image</label>
      <PanelBackgroundImageField
          value={getBackgroundImage(config)}
          onChange={onBackgroundImageChange} />
    </div>
  </div>;
});

export default Immutable.fromJS({
  type: "Text",
  label: "Text & Image",
  titleIsEditable: false,
  variant: "transparent",
  getDefaultData: () => Immutable.Map(),
  getReactComponent: () => Text,
  getEditorReactComponent: () => TextEditor,
  getTitle: component => undefined,
  canFullScreen: false,
  layout: {
    min: {width: 6, height: 1},
    max: {width: 48, height: 48},
    init: {width: 8, height: 1}
  },
  // Rich text needs room to be useful, so new rich panels come in grid-sized
  // (matching the UI/server grid panels); fit-mode keeps the small strip that
  // suited the legacy Heading panel. Falls back to layout.init when absent.
  getInitLayout: (dataConfig) => Immutable.fromJS(
    getTextMode(dataConfig) === "rich"
      ? {width: 24, height: 16}
      : {width: 8, height: 1}
  ),
  applyParentConfig: (parentConfig, dataConfig) => DashboardUtils.applyInheritanceForKeys(parentConfig, dataConfig)
});
